import { useState } from 'react';
import './PricingSection.css';
import { PRICING_PLANS, openCheckout } from '../../constants/pricing';

type BillingPeriod = 'monthly' | 'annually';

const FEATURES = [
  'Personalized review requests',
  'Automated follow-up reminders',
  'AI review responses',
  'CRM integrations',
  'Review reactivation campaigns',
  'Google Business Profile sync',
  'Custom branding',
  'Analytics dashboard',
  'Email & SMS outreach',
  'Multi-location support',
  'Priority onboarding',
  'Dedicated account support',
] as const;

const PRO_FEATURES = [...FEATURES, 'Review Lumen x Paige'] as const;

const PLANS = [
  {
    ...PRICING_PLANS[0],
    subtitle: 'Up to 50 requests per month',
    featured: false,
  },
  {
    ...PRICING_PLANS[1],
    subtitle: '50-100 requests per month',
    featured: true,
  },
  {
    ...PRICING_PLANS[2],
    subtitle: '100-300 requests per month',
    featured: false,
  },
] as const;

function CheckIcon({ inverted }: { inverted?: boolean }) {
  return (
    <span
      className={`pricing-check ${inverted ? 'pricing-check--inverted' : ''}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 12 12" width="10" height="10">
        <path
          fill="currentColor"
          d="M10 3.5 4.75 9.25 2 6.5l1-1 1.75 1.75L9 2.5l1 1z"
        />
      </svg>
    </span>
  );
}

export function PricingSection() {
  const [billing, setBilling] = useState<BillingPeriod>('monthly');

  const periodLabel = billing === 'monthly' ? 'monthly' : 'monthly';

  const handleCheckout = (priceId: string) => {
    openCheckout(priceId);
  };

  return (
    <section id="pricing" className="pricing" aria-labelledby="pricing-heading">
      <div className="container">
        <header className="pricing__header">
          <h2 id="pricing-heading" className="pricing__title">
            Pricing
          </h2>
          <p className="pricing__intro">
            Simple, transparent pricing that scales with your business. Start
            your free trial today and only pay when you&apos;re ready to grow
            your reputation.
          </p>

          <div className="pricing-toggle" role="group" aria-label="Billing period">
            <button
              type="button"
              className={`pricing-toggle__option ${billing === 'monthly' ? 'pricing-toggle__option--active' : ''
                }`}
              aria-pressed={billing === 'monthly'}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`pricing-toggle__option ${billing === 'annually' ? 'pricing-toggle__option--active' : ''
                }`}
              aria-pressed={billing === 'annually'}
              onClick={() => setBilling('annually')}
            >
              Annually
            </button>
          </div>
        </header>

        <div className="pricing__cards">
          {PLANS.map((plan) => {
            const price =
              billing === 'monthly' ? plan.monthly : plan.annually;
            const cardClass = plan.featured
              ? 'pricing-card pricing-card--featured'
              : 'pricing-card';
            const priceId =
              billing === 'monthly' ? plan.priceIdMonthly : plan.priceIdAnnually;

            const features = plan.id === 'pro' ? PRO_FEATURES : FEATURES;

            return (
              <article
                key={plan.id}
                id={plan.id === 'pro' ? 'pricing-pro' : undefined}
                className={cardClass}
              >
                {plan.featured && (
                  <span className="pricing-card__badge">Popular</span>
                )}
                <h3 className="pricing-card__name">{plan.name}</h3>
                <p className="pricing-card__subtitle">{plan.subtitle}</p>
                <p className="pricing-card__price">
                  <span className="pricing-card__amount">${price}</span>
                  <span className="pricing-card__period"> /{periodLabel}</span>
                </p>

                <div className="pricing-card__features">
                  <p className="pricing-card__features-title">
                    What&apos;s included
                  </p>
                  <ul>
                    {features.map((feature) => (
                      <li key={feature}>
                        <CheckIcon inverted={plan.featured} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => handleCheckout(priceId)}
                  className={
                    plan.featured
                      ? 'pricing-card__btn pricing-card__btn--light'
                      : 'pricing-card__btn'
                  }
                >
                  Start Getting Reviews
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
